export type SessionFactSource = 'intake' | 'user_text' | 'user_voice' | 'summary' | 'assumption'

export interface ExtractedSessionFact {
  value: string
  source: SessionFactSource
  confidence: number
  updatedAt: string
  evidence: string[]
}

export interface ExtractedSessionFacts {
  problem?: ExtractedSessionFact | undefined
  volume?: ExtractedSessionFact | undefined
  cost?: ExtractedSessionFact | undefined
  businessEffect?: ExtractedSessionFact | undefined
  desiredResult?: ExtractedSessionFact | undefined
  urgency?: ExtractedSessionFact | undefined
  budget?: ExtractedSessionFact | undefined
  decisionProcess?: ExtractedSessionFact | undefined
  techContext?: ExtractedSessionFact | undefined
  assumptions: Array<{
    value: string
    inputs: string[]
    confidence: number
    editable: boolean
    updatedAt: string
  }>
}

interface ExtractSessionFactsArgs {
  text: string
  existing?: ExtractedSessionFacts | undefined
  at?: string | undefined
  source?: SessionFactSource | undefined
}

type FactKey = Exclude<keyof ExtractedSessionFacts, 'assumptions'>

/** Any fact map with a `value` string. Canonical projection facts qualify. */
export type SessionFactsReadinessInput = {
  [key: string]: { value?: string | null } | null | undefined
}

export interface SessionFactsReadiness {
  coveredCount: number
  totalCount: number
  missing: string[]
  isReady: boolean
}

/** Composer prefill for the Discovery Brief "Answer remaining questions" control. */
export const REMAINING_DISCOVERY_QUESTION =
  'What information do you still need for a useful report?'

const READINESS_FIELDS: Array<{ key: FactKey; label: string }> = [
  { key: 'problem', label: 'problem' },
  { key: 'volume', label: 'volume' },
  { key: 'cost', label: 'cost' },
  { key: 'businessEffect', label: 'business effect' },
  { key: 'desiredResult', label: 'desired result' },
  { key: 'urgency', label: 'urgency' },
  { key: 'budget', label: 'budget' },
  { key: 'decisionProcess', label: 'decision process' },
  { key: 'techContext', label: 'tech context' },
]

const TECH_TERMS = [
  'hubspot',
  'gmail',
  'google analytics',
  'google workspace',
  'ga4',
  'salesforce',
  'slack',
  'notion',
  'excel',
  'google sheets',
  'sheets',
  'spreadsheets',
  'spreadsheet',
  'servicetitan',
  'service titan',
  'zapier',
  'make',
  'airtable',
  'stripe',
  'shopify',
  'crm',
  'erp',
  'microsoft teams',
  'outlook',
  'sharepoint',
  'microsoft 365',
  'office 365',
  'copilot',
  'chatgpt',
  'gemini',
  'visma',
  'tripletex',
  'power bi',
  'xero',
  'fiken',
  'poweroffice',
  'uni micro',
  '24sevenoffice',
  'dynamics 365',
] as const

function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

/** «ca.», «f.eks.», «kr.» end with a dot but do not end the sentence. */
const ABBREVIATION_END = /(?:^|\s)(?:ca|f\.eks|bl\.a|osv|mv|dvs|pga|evt|inkl|ifm|nr|kr|mill|e\.g|i\.e|approx|etc)\.$/i

const MONTH_START =
  /^(?:jan(?:uar|uary)?|feb(?:ruar|ruary)?|mar(?:s|ch)?|apr(?:il)?|mai|may|jun[ie]?|jul[iy]?|aug(?:ust)?|sep(?:t|tember)?|okt(?:ober)?|oct(?:ober)?|nov(?:ember)?|des(?:ember)?|dec(?:ember)?)\b/i

const NB_MONTH =
  'januar|februar|mars|april|mai|juni|juli|august|september|oktober|november|desember'
const EN_MONTH =
  'january|february|march|april|may|june|july|august|september|october|november|december'

/**
 * «Five people do it every week» / «fem personer jobber med det hver uke».
 * The cadence word is only every/each/per/hver, at most six words after the noun.
 * «to» and «en» count only as a number immediately before a Norwegian noun,
 * so «talk to people» is not a headcount. One sentence, not a question.
 */
const SPOKEN_HEADCOUNT_VOLUME = new RegExp(
  String.raw`(?<![\p{L}\p{N}_])(?:(?:\d{1,4}|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|ett|tre|fire|fem|seks|sju|syv|åtte|ni|ti)\s+(?:people|employees|staff|personer|ansatte|folk)|(?:to|en)\s+(?:personer|ansatte|folk))\b(?:\s+\S+){0,6}\s+(?:every|each|per|hver)\s+(?:day|week|month|dag|uke|uka|måned)\b`,
  'iu',
)

const WORK_SUBJECT =
  'invoic\\w*|billing|payroll|reporting|onboarding|quoting|reconciliation|bookkeeping|accounting|close|workflow|process|approval|dispatch\\w*|support|faktur\\w*|regnskap\\w*|arbeidsflyt\\w*|l[øo]nn\\w*|prosess\\w*'

/** A page or a video loading is not the work. «Invoicing takes us too long» is. The subject is the work word, so the match cannot start at «video». */
const PROBLEM_TAKES_TOO_LONG = new RegExp(
  String.raw`\b(?:${WORK_SUBJECT})\s+takes (?:us )?(?:far |way )?too long\b`,
  'iu',
)

/** «Fakturering tar oss mye tid». «Det tar oss lang tid» has no work subject. */
const PROBLEM_TAR_TID = new RegExp(
  String.raw`\b(?:${WORK_SUBJECT})\s+tar (?:oss )?(?:altfor |for |veldig )?(?:mye|lang) tid\b`,
  'iu',
)

const URGENCY_BY_MONTH = new RegExp(
  String.raw`\bby\s+(?:${EN_MONTH})(?:\s+(?!\d{4}\b)\d{1,2}(?:st|nd|rd|th)?)?(?!\s*,?\s*(?:19|20)\d{2}\b)`,
  'i',
)

const URGENCY_NB_DATE = new RegExp(
  String.raw`\b(?:før|innen|ferdig(?:e)? (?:før|innen)|klar(?:t|e)? (?:før|innen|til))\s+\d{1,2}\.\s*(?:${NB_MONTH})\b`,
  'i',
)

/**
 * A past tense or a celebration is not a deadline.
 * «mai-feiringen» stays: the hyphen is the deadline, a separate «feiring» is not.
 */
function isPastOrCelebrationDate(sentence: string): boolean {
  return /\bi fjor\b/i.test(sentence)
    || /\bbursdag\b/i.test(sentence)
    || /(?<!-)\bfeiring/iu.test(sentence)
    || /\bfeirer\b/i.test(sentence)
    || /\bvar\s+ferdig(?:e)?\b/i.test(sentence)
    || /\bvar\s+klar(?:e)?\b/i.test(sentence)
    || /\bble\s+ferdig(?:e)?\b/i.test(sentence)
    || /\bi\s+(?:19|20)\d{2}\b/i.test(sentence)
    || /\b(?:we|i|they|he|she)\s+finished\b/i.test(sentence)
    || /\b(?:was|were)\s+done\b/i.test(sentence)
}

/**
 * #317 E2E 317e (sess_5fa85c1b): «før 1. desember» was cut to «før 1.». A number with a dot and
 * then a lowercase word or a month name is a date or an ordinal («1. desember», «17. okt»,
 * «2. kvartal»), not a sentence end.
 */
function continuesAfterOrdinal(previous: string, next: string): boolean {
  return /(?:^|\s)\d{1,4}\.$/.test(previous) && (/^\p{Ll}/u.test(next) || MONTH_START.test(next))
}

/** Sentence end: «.», «!» or «?» before a space, except after an abbreviation or an ordinal. */
export function splitSentences(text: string): string[] {
  const pieces = text.split(/(?<=[.!?])\s+|\n+/)
  const out: string[] = []
  for (const piece of pieces) {
    const last = out[out.length - 1]
    if (last !== undefined && (ABBREVIATION_END.test(last) || continuesAfterOrdinal(last, piece))) {
      out[out.length - 1] = `${last} ${piece}`
    } else out.push(piece)
  }
  return out
}

function firstSentenceMatching(
  text: string,
  patterns: RegExp[],
  options?: { skipQuestions?: boolean; rejectSentence?: (sentence: string) => boolean }
): string | null {
  const sentences = splitSentences(text)
    .map(normalizeWhitespace)
    .filter(sentence => sentence.length > 0)
    // A question is not a stated fact ("what would we need to prepare?").
    .filter(sentence => !(options?.skipQuestions && sentence.endsWith('?')))
    .filter(sentence => !options?.rejectSentence?.(sentence))

  for (const pattern of patterns) {
    const sentence = sentences.find(candidate => pattern.test(candidate))
    if (sentence) return sentence.slice(0, 220)
  }

  return null
}

function buildFact(value: string, at: string, source: SessionFactSource): ExtractedSessionFact {
  const normalized = normalizeWhitespace(value)
  return {
    value: normalized,
    source,
    confidence:
      source === 'user_text' || source === 'user_voice' || source === 'intake' ? 0.9 : 0.55,
    updatedAt: at,
    evidence: [normalized],
  }
}

function shouldKeepExisting(
  existing: ExtractedSessionFact | undefined,
  next: ExtractedSessionFact,
): boolean {
  if (!existing?.value) return false
  if (existing.source === 'intake' && next.source !== 'intake') return true
  if (existing.confidence > next.confidence) return true
  return existing.value.length >= next.value.length && existing.confidence === next.confidence
}

function setFact(
  facts: Partial<ExtractedSessionFacts>,
  existing: ExtractedSessionFacts | undefined,
  key: FactKey,
  value: string | null,
  at: string,
  source: SessionFactSource
) {
  if (!value) return
  const next = buildFact(value, at, source)
  facts[key] = shouldKeepExisting(existing?.[key], next) ? (existing?.[key] ?? next) : next
}

function extractCurrencyFact(text: string, patterns: RegExp[]): string | null {
  for (const pattern of patterns) {
    const match = text.match(pattern)
    if (match?.[0]) return normalizeWhitespace(match[0]).slice(0, 180)
  }
  return null
}

const INTERNAL_TOOL_LINE_RE = /^\s*Current tool context:/i

/**
 * The techContext fact is an internal line («Current tool context: gemini»), not the visitor's
 * words. N9 (17.10): the brief printed it as a conversation fact. Report code skips it.
 */
export function isInternalToolLine(value: string | null | undefined): boolean {
  return INTERNAL_TOOL_LINE_RE.test(value ?? '')
}

function extractTechContext(text: string): string | null {
  const lower = text.toLowerCase()
  const tools: string[] = TECH_TERMS.filter(term => lower.includes(term))
  // «Teams» as a product name. Lowercase "teams" is English prose, not a tool.
  if (!tools.includes('microsoft teams') && /\bTeams\b/.test(text)) tools.push('teams')
  const headcount = text.match(/\b(\d{1,3})\s*(dispatchers?|techs?|technicians?)\b/i)
  const parts: string[] = []
  if (tools.length > 0) parts.push(`Current tool context: ${tools.join(', ')}`)
  if (headcount?.[0]) parts.push(normalizeWhitespace(headcount[0]))
  if (parts.length === 0) return null
  return parts.join('. ')
}

export function extractSessionFactsFromText({
  text,
  existing,
  at = new Date().toISOString(),
  source = 'user_text',
}: ExtractSessionFactsArgs): Partial<ExtractedSessionFacts> {
  const cleanText = normalizeWhitespace(text)
  if (!cleanText) return {}

  const facts: Partial<ExtractedSessionFacts> = {}

  setFact(
    facts,
    existing,
    'problem',
    firstSentenceMatching(cleanText, [
      /\b(we|i|our team).*\b(need|struggle|challenge|problem|pain|bottleneck|stuck|manual|slow|miss(?:ed|ing)|spend too much)\b/i,
      /\b(manual|slow|miss(?:ed|ing)|too much time|bottleneck|rework|backlog|no-?shows?|drowning)\b/i,
      /\btoo slow\b/i,
      PROBLEM_TAKES_TOO_LONG,
      /\bquote[- ]to[- ]cash\b|\bthe real bottleneck\b|\bnot scheduling\b/i,
      /\b(?:altfor mye tid|for mye tid|mye tid på|tar for lang tid|tidkrevende|dobbeltarbeid|for mange feil|henger etter|for generelle?|ikke tilpasset|for lite tilpasset)\b/i,
      PROBLEM_TAR_TID,
      /\b(sliter|dør|mister|taper|flaskehals|manuell|tregt|svartid|raskere)\b/i,
      /\bdispatchers?\b/i,
    ], { skipQuestions: true }),
    at,
    source
  )

  setFact(
    facts,
    existing,
    'volume',
    extractCurrencyFact(cleanText, [
      /\b\d{1,4}(?:,\d{3})?\s*(?:jobs|work orders?|dispatches?|calls|tickets|emails|leads|orders|invoices|reports|hours|minutes)\s*(?:per|a|each|\/)?\s*(?:day|week|month|quarter)?\b/i,
      /\b\d{1,4}(?:,\d{3})?\s*(?:jobs|work orders?|dispatches?)\b/i,
      /\b(?:daily|weekly|monthly)\s+volume\s+(?:is|of)?\s*\d{1,4}(?:,\d{3})?/i,
      // Norwegian: «300 henvendelser i uka», «40 kurs i måneden».
      /\b\d{1,5}(?:[ \u00a0]\d{3})?\s*(?:henvendelser|saker|kurs|deltakere|e-poster|eposter|samtaler|bestillinger|fakturaer|ordrer?|kunder|søknader|tilbud|timer)\s+(?:i|per|pr\.?|hver|om)\s+(?:dagen|dag|uka|uken|uke|måneden|måned|mnd|året|år|kvartalet|kvartal)(?![a-zæøå])/i,
    ]) ??
      firstSentenceMatching(cleanText, [SPOKEN_HEADCOUNT_VOLUME], { skipQuestions: true }),
    at,
    source
  )

  setFact(
    facts,
    existing,
    'cost',
    extractCurrencyFact(cleanText, [
      /\b(?:costs?|los(?:e|ing)|wastes?|spend(?:ing)?)\s+(?:us\s+)?(?:about\s+|around\s+)?[$€£]?\d[\d,]*(?:k|m)?(?:\s*(?:per|a|each|\/)\s*(?:day|week|month|year))?/i,
      /[$€£]\d[\d,]*(?:k|m)?(?:\s*\/\s*(?:year|yr|month|mo|week|wk|day))?/i,
      /[$€£]\d[\d,]*(?:k|m)?\s*(?:in\s+)?(?:lost|wasted|manual|support|ops)?\s*(?:cost|revenue|time)?/i,
      // #317 E2E 317d (sess_a14e4e6c): «40,000 NOK» gave «000 NOK»; a comma group is part of the number.
      /(?<![\d,.])\d{1,3}(?:[\s,.\u00a0]\d{3})+(?:k\b)?\s*(?:kroner|kr\.?|nok)\b|(?<![\d,.])\d+(?:k)?\s*(?:kroner|kr\.?|nok)\b/i,
      /\b(?:\d+|en|ett|et|to|tre|fire|fem|seks|halvt?)\s+(?:årsverk|stillinger|fulltidsstillinger)(?![a-zæøå])/i,
    ]),
    at,
    source
  )

  setFact(
    facts,
    existing,
    'businessEffect',
    firstSentenceMatching(cleanText, [
      /\b(los(?:e|ing)|miss(?:ed|ing)|churn|delay(?:ed|s)?|conversion|customers? wait|revenue|margin|quality|sla|risk)\b/i,
      /\b(?:mister (?:kunder|salg|oppdrag|deltakere)|taper (?:kunder|salg|penger|oppdrag)|går glipp av|kundene venter|frafall|omdømme|klager)(?![a-zæøå])/i,
    ], { skipQuestions: true }),
    at,
    source
  )

  setFact(
    facts,
    existing,
    'desiredResult',
    firstSentenceMatching(cleanText, [
      /\b(?:goal|want|need|looking to|trying to|hope to|so that|outcome|result)\b/i,
      /\b(?:målet|maalet|mål er|svartid|raskere)\b/i,
      /(?:^|\s)(?:vi ønsker|ønsker vi|vi vil gjerne|vi vil at|vi håper|håper å|skal bli bedre)(?![a-zæøå])/i,
    ]),
    at,
    source
  )

  setFact(
    facts,
    existing,
    'urgency',
    firstSentenceMatching(cleanText, [
      /\b(asap|urgent|this quarter|next quarter|next month|this month|month-end|90-?\s*days?|before|by q[1-4]|6-8 weeks?|six to eight weeks?|within \d+ weeks?)\b/i,
    ], { rejectSentence: isPastOrCelebrationDate }) ??
      firstSentenceMatching(cleanText, [URGENCY_BY_MONTH], {
        skipQuestions: true,
        rejectSentence: isPastOrCelebrationDate,
      }) ??
      firstSentenceMatching(cleanText, [
        /\b(?:haster|snarest|så fort som mulig|før (?:jul|sommeren|påske|nyttår|ferien|årsskiftet|helgen)|innen (?:\d+|en|ett|to|tre|fire|fem|seks) (?:dager|uker|måneder)|innen (?:januar|februar|mars|april|mai|juni|juli|august|september|oktober|november|desember)|neste (?:uke|måned|kvartal)|denne (?:uka|uken|måneden)|i (?:høst|vår)|på plass til)(?![a-zæøå])/i,
        URGENCY_NB_DATE,
      ], { skipQuestions: true, rejectSentence: isPastOrCelebrationDate }),
    at,
    source
  )

  setFact(
    facts,
    existing,
    'budget',
    extractCurrencyFact(cleanText, [
      /(?<![\d,.])\d+(?:[\s,.\u00a0]\d{3})*(?:k)?\s*(?:kroner|kr\.?|nok).{0,24}(?:måned|mnd|mo)/i,
      // «Budsjett 50 000 kroner»: «k» of «kroner» is not the thousands suffix (#317 retest, a911a1af).
      // «Budget is around 40,000 NOK» (317d C): «is» and «around» both stand before the number.
      /\b(?:budget|spend|investment|approved|budsjett)\s+(?:(?:is|of|around|about|up to|between|på|er|rundt|ca\.?)\s+){0,2}[$€£]?\d[\d\s,]*(?:(?:k|m)\b)?(?:\s*(?:kroner|kr\.?|nok)\b)?(?:\s*(?:-|to)\s*[$€£]?\d[\d,]*(?:(?:k|m)\b)?)?/i,
      /\b[$€£]\d[\d,]*(?:k|m)?(?:\s*(?:-|to)\s*[$€£]?\d[\d,]*(?:k|m)?)?\s+(?:budget|approved|available|investment|budsjett)\b/i,
      // Norwegian: «budsjett på rundt 100 000 kroner», «satt av 80 000 kr».
      /\b(?:budsjett(?:et)?|ramme(?:n)?|satt av|avsatt|kan bruke)\b[^.?!\d]{0,30}\d+(?:[\s,.\u00a0]\d{3})*(?:k)?\s*(?:kroner|kr\.?|nok|millioner|mill\.?)/i,
    ]),
    at,
    source
  )

  setFact(
    facts,
    existing,
    'decisionProcess',
    firstSentenceMatching(cleanText, [
      /\b(decision|decide|approval|approve|stakeholder|procurement|board|ceo|cto|cfo|gm|greenlight|budget owner|sign.?off)\b/i,
    ]) ??
      firstSentenceMatching(cleanText, [
        /\b(?:daglig leder|styret|ledergruppen|ledelsen|bestemmer|beslutter|beslutning|godkjenne|godkjenner|signere|innkjøp)(?![a-zæøå])/i,
      ], { skipQuestions: true }),
    at,
    source
  )

  setFact(facts, existing, 'techContext', extractTechContext(cleanText), at, source)

  return facts
}

const READINESS_LABEL_NB: Record<string, string> = {
  problem: 'problem',
  volume: 'volum',
  cost: 'kostnad',
  'business effect': 'forretningseffekt',
  'desired result': 'ønsket resultat',
  urgency: 'hastverk',
  budget: 'budsjett',
  'decision process': 'beslutningsprosess',
  'tech context': 'teknisk kontekst',
  effect: 'effekt',
}

/** Card line under Sporer. The model prompt keeps the English inventory. */
export function localizeReadinessMissing(
  missing: readonly string[],
  locale: 'en' | 'nb',
): string[] {
  if (locale !== 'nb') return [...missing]
  return missing.map(label => READINESS_LABEL_NB[label] ?? label)
}

export function getSessionFactsReadiness(
  facts: SessionFactsReadinessInput | undefined,
): SessionFactsReadiness {
  const covered = READINESS_FIELDS.filter(field => Boolean(facts?.[field.key]?.value))
  const missing = READINESS_FIELDS
    .filter(field => !facts?.[field.key]?.value)
    .map(field => field.label)

  return {
    coveredCount: covered.length,
    totalCount: READINESS_FIELDS.length,
    missing,
    isReady: covered.length >= 6,
  }
}

function normalizeAsk(text: string): string {
  return text.replace(/[’']/g, "'").replace(/\s+/g, ' ').trim()
}

/**
 * Visitor asked which brief fields are still open.
 * This is the button prefill and the phrasings that produced the 2/9 desync.
 */
export function isRemainingDiscoveryAsk(text: string | null | undefined): boolean {
  const value = normalizeAsk(text ?? '')
  if (!value) return false
  if (value.toLowerCase() === REMAINING_DISCOVERY_QUESTION.toLowerCase()) return true
  return (
    /\bwhat information (?:do you|do i|is) still need\b/i.test(value) ||
    /\bwhat (?:else )?(?:do|did) you still need\b/i.test(value) ||
    /\bwhat (?:do|did) i still need to (?:answer|tell|give|cover)\b/i.test(value) ||
    /\bwhat questions? (?:do i|do you|are) still\b/i.test(value) ||
    /\bwhat discovery questions?\b/i.test(value) ||
    /\bdiscovery questions? (?:do i|are) still\b/i.test(value) ||
    /\blist the remaining (?:fields|questions)\b/i.test(value) ||
    /\bremaining discovery questions\b/i.test(value) ||
    /\bwhat(?:'s| is) still (?:missing|open) (?:for|on) (?:the |a )?(?:brief|report|discovery)\b/i.test(value) ||
    /\bwhich (?:discovery )?(?:questions?|fields?) (?:are )?(?:still )?(?:left|open|remaining)\b/i.test(value) ||
    /\banything else (?:do )?you (?:still )?need\b/i.test(value) ||
    /\bhva (?:mangler|gjenstår|er fortsatt åpent)\b/i.test(value) ||
    /\bhvilke (?:spørsmål|felt) (?:gjenstår|mangler|er åpne)\b/i.test(value) ||
    /\bhva trenger du fortsatt\b/i.test(value)
  )
}

/** Model claimed the discovery question list is empty. */
export function deniesRemainingDiscovery(text: string): boolean {
  return (
    /\b(?:do not|don't|dont|does not|doesn't)\s+have any remaining (?:discovery )?questions\b/i.test(text) ||
    /\bno remaining discovery questions\b/i.test(text) ||
    /\b(?:there are|there aren't|there are not) (?:any )?remaining discovery questions\b/i.test(text) ||
    /\bno (?:more )?discovery questions left\b/i.test(text) ||
    /\bi have no (?:more |remaining )?(?:discovery )?questions\b/i.test(text)
  )
}

/** Spoken answer. Same missing labels as the Discovery Brief card. */
export function formatRemainingDiscoveryReply(
  readiness: Pick<SessionFactsReadiness, 'coveredCount' | 'totalCount' | 'missing'>,
  locale: 'en' | 'nb' = 'en',
): string {
  const count = `${readiness.coveredCount}/${readiness.totalCount}`
  const missing = localizeReadinessMissing(readiness.missing, locale).join(', ')
  if (locale === 'nb') {
    if (readiness.missing.length === 0) {
      return `Dekningen er ${count}. Ingen felt er åpne. Lag briefen med det vi har (PDF), og book en samtale hvis du vil.`
    }
    return `Dekningen er ${count}. Du kan lage discovery-briefen nå — resten er valgfritt. Åpent: ${missing}.`
  }
  if (readiness.missing.length === 0) {
    return `Coverage is ${count}. No discovery fields are still open. Generate the brief with current information (PDF), then book a call if you want one.`
  }
  return `Coverage is ${count}. You can generate the discovery brief now — remaining fields are optional. Still open: ${readiness.missing.join(', ')}.`
}

/** Card sentence under the N/9 count. Tracking line is `missing` itself. */
export function formatDiscoveryCoverageCardLine(
  readiness: Pick<SessionFactsReadiness, 'missing'>,
  locale: 'en' | 'nb' = 'en',
): string {
  if (locale === 'nb') {
    if (readiness.missing.length === 0) {
      return 'Lag briefen (PDF), og book en samtale. Dekningen er komplett.'
    }
    return 'Lag briefen (PDF), og book en samtale. Resten er valgfritt — du kan lage den nå.'
  }
  if (readiness.missing.length === 0) {
    return 'Generate the brief (PDF), then book a call. Coverage is complete.'
  }
  return 'Generate the brief (PDF), then book a call. Remaining fields are optional — you can generate now.'
}

/** Live / discovery instruction. One inventory with the card. */
export function formatDiscoveryCoveragePromptBlock(readiness: SessionFactsReadiness): string {
  const missing = readiness.missing.length > 0 ? readiness.missing.join(', ') : 'none'
  return `BRIEF COVERAGE (Discovery Brief card — ${readiness.coveredCount}/${readiness.totalCount}. This is not the 6 survey categories and not the follow-up budget):
- Missing: ${missing}
- Do not interview these fields unless they ask what is still needed, what remaining discovery questions there are, or what information you still need for the report.
- If they ask and Missing is not none: say ${readiness.coveredCount}/${readiness.totalCount}, name every missing label, and say the fields are optional so they can generate now. Never say you have no remaining discovery questions.
- If Missing is none: say coverage is complete and they can generate.
- A stage move to EXPLORING / scoring does not close this list.
- ${BRIEF_COVERAGE_SIDE_QUEST_RULE}`
}

export const BRIEF_COVERAGE_RULE =
  'BRIEF COVERAGE is the Discovery Brief card inventory (problem, volume, cost, business effect, desired result, urgency, budget, decision process, tech context) — not the 6 survey categories. Do not interview those fields unless they ask what remains. If they ask and any field is missing, name the missing labels and the N/9 count, and say the fields are optional. Never say you have no remaining discovery questions while any field is missing. Advancing to EXPLORING does not close the list.'

/**
 * Owner visual-debug / EXPLORING side quest. A zero clarification budget and
 * "stay on the visual issue" are not an empty Discovery Brief.
 */
export const BRIEF_COVERAGE_SIDE_QUEST_RULE =
  'SIDE QUEST: A visual-observation debug, owner test, LOCATION, stage EXPLORING, or follow-up budget of 0 does not close the Discovery Brief. If they ask what remains, answer from BRIEF COVERAGE even mid visual debug: N/9, every missing label, fields optional. Never treat "clarifying questions remaining: 0" or the visual issue as an empty discovery list.'

/**
 * Replace a false "no remaining discovery questions" claim with the card inventory.
 * Keeps any later sentence (visual observation, Monday action) after the inventory.
 */
export function reconcileDeniedRemainingDiscovery(
  text: string,
  readiness: Pick<SessionFactsReadiness, 'coveredCount' | 'totalCount' | 'missing'> | undefined,
  locale: 'en' | 'nb' = 'en',
): string {
  if (!text || !readiness || readiness.missing.length === 0) return text
  if (!deniesRemainingDiscovery(text)) return text
  const reply = formatRemainingDiscoveryReply(readiness, locale)
  const kept = text
    .split(/(?<=[.!?])\s+/)
    .map(sentence => sentence.trim())
    .filter(sentence => sentence.length > 0 && !deniesRemainingDiscovery(sentence))
  if (kept.length === 0) return reply
  return `${reply} ${kept.join(' ')}`
}

/** Null when this turn is not a remaining-discovery ask. */
export function resolveRemainingDiscoveryReply(
  message: string | null | undefined,
  facts: SessionFactsReadinessInput | undefined,
  locale: 'en' | 'nb' = 'en',
): string | null {
  if (!isRemainingDiscoveryAsk(message)) return null
  return formatRemainingDiscoveryReply(getSessionFactsReadiness(facts), locale)
}

/** A money value the old patterns cut: «50 000 k» (the «k» of «kroner») or «000 NOK» (the «40,» lost). */
function isCutMoneyValue(value: string): boolean {
  return /\d\s+k$/i.test(value) || /^0\d/.test(value)
}

type MoneyInput = {
  sessionFacts?: Partial<Record<'budget' | 'cost', { value?: string | null } | undefined>> | null | undefined
  transcript?: ReadonlyArray<{ role: string; state?: string; content: unknown }> | null | undefined
}

/**
 * #317 round 3 (sess_3bda733a): Fly wrote «Budsjett 50 000 k» with the old pattern (the «k» of
 * «kroner»), and stored facts keep it. #317 E2E 317d (sess_a14e4e6c): «around 40,000 NOK» was
 * stored as cost «000 NOK». A stored value cut like that is read again from the visitor's own lines
 * with the current pattern. A real «50k» has no space and is kept. A cut value with no better
 * reading is shown without the stray «k», or not at all when its number is lost.
 */
export function visitorMoneyValue(input: MoneyInput, key: 'budget' | 'cost'): string {
  const stored = (input.sessionFacts?.[key]?.value ?? '').trim()
  if (!isCutMoneyValue(stored)) return stored
  for (const entry of input.transcript ?? []) {
    if (entry.role !== 'user' || entry.state === 'interim' || typeof entry.content !== 'string') continue
    const hit = extractSessionFactsFromText({ text: entry.content })[key]?.value?.trim()
    if (hit && !isCutMoneyValue(hit)) return hit
  }
  return /^0\d/.test(stored) ? '' : stored.replace(/\s+k$/i, '')
}

export function visitorBudgetValue(input: MoneyInput): string {
  return visitorMoneyValue(input, 'budget')
}

const LEADING_GREETING_RE =
  /^(?:hei(?:\s+igjen|\s+og\s+takk)?|heisann|hallo|hi(?:\s+there)?|hello|hey|god\s+(?:morgen|dag|kveld)|good\s+(?:morning|afternoon|evening))(?:\s+\p{Lu}[\p{L}-]*)?\s*[,!.:–—-]+\s*/iu

/**
 * #317 E2E 317d (sess_01df9209, sess_a14e4e6c): the problem fact is the visitor's first line,
 * «Hei, vi lager kurs …» / «Hi, we run a print shop …». The brief cut it at its first comma and
 * printed «Hei.» as the reason, and «Hi, we run a print shop» as the title. A leading greeting is
 * no part of the problem; the rest of the line starts with a capital.
 */
export function stripLeadingGreeting(text: string): string {
  const trimmed = text.trim()
  const rest = trimmed.replace(LEADING_GREETING_RE, '')
  if (rest === trimmed || !rest) return trimmed
  return rest.charAt(0).toLocaleUpperCase() + rest.slice(1)
}
